/**
 * Xtram brand identity — Visual Identity Manual 2026
 * Single source of truth for colors, type, voice, logos, and UI rules.
 */

export const XTRAM_BRAND = {
  name: 'Xtram',
  legalName: 'Xtram',
  product: 'Xtram',
  year: 2026,
  essence:
    'AI-powered accounting bridge between accountants and clients. Clean, trustworthy, modern FinTech — not noisy or decorative.',
  feel: ['trustworthy', 'clean', 'unified', 'modern FinTech'],
  values: [
    { id: 'trust', label: 'Trust First' },
    { id: 'innovation', label: 'Smart Innovation' },
    { id: 'simplicity', label: 'Simplicity Wins' },
    { id: 'agility', label: 'Agility for Everyone' },
  ],
};

/** Official palette (prefer these over legacy #304FFF in new UI). */
export const XTRAM_COLORS = {
  // Primary
  shockingBlue: '#3366FF',
  classicOrange: '#F9AA2A',
  mirage: '#141E2B',
  white: '#FFFFFF',

  // Secondary / semantic
  vividCyan: '#00F2FE',
  watermelon: '#EF476F',
  caramelGold: '#EDB458',

  // Legacy app blue (existing SVGs / older screens)
  shockingBlueApp: '#304FFF',
  shockingBlueHover: '#263FCC',
  classicOrangeHover: '#E09926',

  // Surfaces
  canvas: '#FFFFFF',
  canvasTint: '#FCFCFF',
  overlaySoft: 'rgba(20, 30, 43, 0.45)',

  // UI status (login / register success copy)
  successGreen: 'rgba(44, 216, 82, 1)',
  mirageMuted: 'rgba(20, 30, 43, 0.7)',
};

/** Semantic aliases for components. */
export const XTRAM_SEMANTIC = {
  primary: XTRAM_COLORS.shockingBlue,
  primaryHover: XTRAM_COLORS.shockingBlueHover,
  accent: XTRAM_COLORS.classicOrange,
  accentHover: XTRAM_COLORS.classicOrangeHover,
  text: XTRAM_COLORS.mirage,
  textOnDark: XTRAM_COLORS.white,
  textOnBlue: XTRAM_COLORS.white,
  canvas: XTRAM_COLORS.canvas,
  success: XTRAM_COLORS.vividCyan,
  error: XTRAM_COLORS.watermelon,
  warning: XTRAM_COLORS.caramelGold,
  pending: XTRAM_COLORS.caramelGold,
  delete: XTRAM_COLORS.watermelon,
  growth: XTRAM_COLORS.vividCyan,
};

export const XTRAM_TYPOGRAPHY = {
  fonts: {
    en: '"Plus Jakarta Sans", Arial, sans-serif',
    he: '"Heebo", "Arial Hebrew", Arial, sans-serif',
    ar: '"Alexandria", Cairo, Arial, sans-serif',
  },
  weights: {
    light: 300,
    regular: 400,
    medium: 500,
    semiBold: 600,
    bold: 700,
    extraBold: 800,
  },
  roles: {
    display: { weight: 800, letterSpacing: '-0.02em' },
    heading: { weight: 700, letterSpacing: '-0.01em' },
    subheading: { weight: 600, letterSpacing: '0' },
    body: { weight: 400, letterSpacing: '0' },
    label: { weight: 600, letterSpacing: '0.01em' },
    caption: { weight: 400, letterSpacing: '0.01em' },
  },
  scale: {
    xs: '0.75rem', // 12
    sm: '0.875rem', // 14
    md: '1rem', // 16
    lg: '1.125rem', // 18
    xl: '1.25rem', // 20
    '2xl': '1.5rem', // 24
    '3xl': '1.875rem', // 30
    '4xl': '2.25rem', // 36
    '5xl': '2.5rem', // 40
  },
  /** Pick font stack from i18n language code. */
  fontForLang(lang = 'en') {
    const code = String(lang).toLowerCase().slice(0, 2);
    if (code === 'he') return this.fonts.he;
    if (code === 'ar') return this.fonts.ar;
    return this.fonts.en;
  },
};

export const XTRAM_RADIUS = {
  sm: '6px',
  md: '10px',
  lg: '16px',
  xl: '24px',
  pill: '999px',
};

export const XTRAM_SPACE = {
  xs: '4px',
  sm: '8px',
  md: '16px',
  lg: '24px',
  xl: '32px',
  '2xl': '48px',
  '3xl': '64px',
};

export const XTRAM_LOGO_RULES = {
  wordmarkMinWidth: 120,
  wordmarkMinHeight: 32,
  clearSpaceNote: 'Clear space = 1× height of lowercase “x” on all sides',
  appIconPadding: '25%',
  doNots: [
    'Do not rotate the logo',
    'Do not stretch or skew',
    'Do not recolor outside approved variants',
    'Do not add drop shadows, glows, or 3D',
    'Do not place in unapproved boxes',
    'Do not shrink wordmark below 120×32 — use mark only',
  ],
  surfaces: {
    light: 'Full color X + Mirage “tram”',
    dark: 'Full color X + white “tram”',
    blue: 'On-blue / white wordmark',
    photo: 'Solid white logo for saturated / photo overlays',
    mono: 'Solid Mirage #141E2B watermark',
    icon: 'X mark only under 120px',
  },
};

/**
 * Brand voice & product copy (UI / marketing).
 * Prefer i18n keys in product screens; use these for defaults and marketing.
 */
export const XTRAM_COPY = {
  voice: {
    tone: ['clear', 'confident', 'helpful', 'human'],
    principles: [
      'Lead with clarity — one idea per sentence',
      'Prefer plain language over jargon',
      'Be trustworthy: precise numbers, no hype',
      'Be concise: short labels, short empty states',
      'RTL-aware: Hebrew and Arabic are first-class',
    ],
    avoid: [
      'Buzzwords without meaning',
      'Aggressive urgency (“Act now!!!”)',
      'Blaming the user for errors',
      'Emoji clutter in product UI',
    ],
  },
  taglines: {
    en: {
      primary: 'Accounting, connected.',
      supporting: 'AI-powered tools that bridge accountants and clients.',
    },
    he: {
      primary: 'הנהלת חשבונות, מחוברת.',
      supporting: 'כלים מבוססי AI שמחברים רואי חשבון ולקוחות.',
    },
    ar: {
      primary: 'محاسبة متصلة.',
      supporting: 'أدوات مدعومة بالذكاء الاصطناعي تربط المحاسبين والعملاء.',
    },
  },
  product: {
    en: {
      welcomeBack: 'Welcome back',
      signIn: 'Sign in',
      continue: 'Continue',
      save: 'Save',
      cancel: 'Cancel',
      errorGeneric: 'Something went wrong. Please try again.',
      successGeneric: 'Done successfully.',
    },
    he: {
      welcomeBack: 'ברוך הבא',
      signIn: 'התחברות',
      continue: 'המשך',
      save: 'שמירה',
      cancel: 'ביטול',
      errorGeneric: 'משהו השתבש. נסו שוב.',
      successGeneric: 'בוצע בהצלחה.',
    },
    ar: {
      welcomeBack: 'مرحباً بعودتك',
      signIn: 'تسجيل الدخول',
      continue: 'متابعة',
      save: 'حفظ',
      cancel: 'إلغاء',
      errorGeneric: 'حدث خطأ. يرجى المحاولة مرة أخرى.',
      successGeneric: 'تم بنجاح.',
    },
  },
  alt: {
    logo: 'Xtram',
    mark: 'Xtram mark',
  },
};

export const XTRAM_UI = {
  patterns: [
    'Clean canvas — white/light surfaces, Mirage text, Shocking Blue CTA',
    'Flat & crisp — no logo shadows, glows, or 3D',
    'High contrast on photo overlays',
    'Blue = trust/action · Orange = accent/energy · Mirage = type · White = space',
    'Semantic states — Cyan success, Watermelon error, Gold pending',
    'Generous spacing; one job per section',
    'RTL-native for Hebrew and Arabic',
  ],
  button: {
    primaryBg: XTRAM_SEMANTIC.primary,
    primaryText: XTRAM_COLORS.white,
    accentBg: XTRAM_SEMANTIC.accent,
    radius: XTRAM_RADIUS.md,
  },
  scroll: {
    overflowY: 'auto',
    overflowX: 'hidden',
    padding: '2.8px',
    scrollbarWidth: 'thin',
    scrollbarColor: `${XTRAM_COLORS.mirage} transparent`,
    webkitOverflowScrolling: 'touch',
  },
};

/** Flat object for convenience: import { brand } from '.../xtram' */
export const brand = {
  ...XTRAM_BRAND,
  colors: XTRAM_COLORS,
  semantic: XTRAM_SEMANTIC,
  typography: XTRAM_TYPOGRAPHY,
  radius: XTRAM_RADIUS,
  space: XTRAM_SPACE,
  logoRules: XTRAM_LOGO_RULES,
  copy: XTRAM_COPY,
  ui: XTRAM_UI,
};

export default brand;
